// Dragon Keep: she IS the dragon. Knights march down three lanes toward her hoard; she turns
// and breathes fire, ice or lightning, sweeps her tail, and gusts her wings. Losing a wave just
// restarts it with one fewer knight. Hoard gold is in-run only and buys upgrades between waves.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import { pick, shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { makeWaves, KIND_SPEED, KIND_HP, WAVES, type Wave, type KnightKind } from './waves';
import { QUIZ } from '@content/science/quiz';
import { CLUE_WORDS } from '@content/words/clues';

const LANES = 3;
const COINS_PER_WAVE = 5;
const PALETTES = [
  { name: 'Ember', body: '#e2574c', belly: '#f5c542', wing: '#b93535' },
  { name: 'Moss', body: '#5fbf6a', belly: '#f6f3ec', wing: '#2e7d4f' },
  { name: 'Sky', body: '#4f8fe6', belly: '#bfe8ff', wing: '#256abf' },
  { name: 'Violet', body: '#9b6be6', belly: '#ee86b7', wing: '#6250d6' },
];

type Weapon = 'fire' | 'ice' | 'lightning';
interface Knight { kind: KnightKind; lane: number; y: number; hp: number; state: 'march' | 'carry' | 'flee'; stun: number; hop: number; lob: number }
interface Rock { lane: number; side: -1 | 1; t: number; dur: number }
interface Fx { kind: 'fire' | 'ice' | 'bolt' | 'sweep' | 'gust' | 'roar'; lane: number; t: number }
interface Question { q: string; choices: string[]; answer: number; why: string }

function mount(host: HTMLElement, ctx: GameContext) {
  const waves = makeWaves(ctx.rng);
  let palette = PALETTES[0];
  let waveIdx = 0;
  let wave: Wave | null = null;
  let waveT = 0;
  let spawned = 0;
  let knights: Knight[] = [];
  let rocks: Rock[] = [];
  let fx: Fx[] = [];
  let coinsLost = 0;
  let totalLost = 0;
  let routed = 0;
  let gold = 0;
  let wavesPlayed = 0, wavesCleared = 0;
  let restartPenalty = 0;          // knights removed from the current wave after a loss
  let frozen = [0, 0, 0];          // seconds left per lane
  let armed: Weapon = 'fire';
  let facingLane = 1;
  let breathT = 0;                 // dragon breathing animation
  let stun = 0;                    // dragon stunned by a rock
  let side: -1 | 1 = 1;            // which side of the hoard the dragon perches on
  let flap = 0;
  let phase: 'pick' | 'play' | 'break' | 'done' = 'pick';
  let scrollUsed = false;
  const upgrades = { recharge: 0, wideFire: false, secondTail: false, gemShield: 0 };
  const rechargeMax: Record<string, number> = { fire: 1.3, ice: 8, lightning: 12, tail: 5 };
  const charge: Record<string, number> = { fire: 0, ice: 0, lightning: 0, tail: 0 };
  let tailCharges = 1;
  let gustUsed = false;
  let msgT = 0;
  const t0 = performance.now();
  const used = new Set<string>();

  // ---------- DOM ----------
  const head = h('div.row', { style: { justifyContent: 'space-between' } },
    h('span', null, 'Wave ', h('strong', null, '-'), ` of ${WAVES}`),
    h('span.small', null, 'Coins safe: ', h('strong', null, String(COINS_PER_WAVE))),
    h('span.small', null, 'Gold: ', h('strong', null, '0')),
  );
  const stage = h('div');
  const panel = h('div.stack');
  const controls = h('div.stack', { style: { gap: '6px' } });
  host.append(head, stage, controls, panel);
  const S = makeSurface(stage, { aspect: 1.0, draw: true });

  const weaponBtn = (w: Weapon, label: string, color: string) => {
    const ring = h('span', { style: { width: '34px', height: '34px', borderRadius: '50%', display: 'inline-block', background: color, position: 'relative' } });
    const b = h('button.btn', { style: { flexDirection: 'column', gap: '2px', minHeight: '64px', padding: '6px 4px', flex: '1', fontSize: '14px' }, onclick: () => { armed = w; ctx.sound('tick'); renderControls(); ctx.say(`${label} armed. Tap a lane.`); } }, ring, label);
    return { b, ring };
  };
  const fire = weaponBtn('fire', 'Fire', '#f08a3c');
  const ice = weaponBtn('ice', 'Ice', '#7fd0f0');
  const bolt = weaponBtn('lightning', 'Lightning', '#f5c542');
  const tailRing = h('span', { style: { width: '34px', height: '34px', borderRadius: '50%', display: 'inline-block', background: '#9b6be6' } });
  const tailBtn = h('button.btn', { style: { flexDirection: 'column', gap: '2px', minHeight: '64px', padding: '6px 4px', flex: '1', fontSize: '14px' }, onclick: tailSweep }, tailRing, 'Tail Sweep');
  const gustBtn = h('button.btn', { style: { flexDirection: 'column', gap: '2px', minHeight: '64px', padding: '6px 4px', flex: '1', fontSize: '14px' }, onclick: wingGust }, h('span', { style: { width: '34px', height: '34px', borderRadius: '50%', display: 'inline-block', background: '#3bb3a9' } }), 'Wing Gust');
  const row1 = h('div.row', { style: { gap: '6px' } }, fire.b, ice.b, bolt.b);
  const row2 = h('div.row', { style: { gap: '6px' } }, tailBtn, gustBtn);
  controls.append(row1, row2);
  controls.style.display = 'none';

  function ringStyle(el: HTMLElement, frac: number, color: string, ready: boolean) {
    const pct = Math.round(Math.max(0, Math.min(1, frac)) * 100);
    el.style.background = `conic-gradient(${color} ${pct}%, rgba(0,0,0,.12) 0)`;
    el.style.outline = ready ? `3px solid ${color}` : 'none';
    el.style.outlineOffset = '2px';
  }
  function renderControls() {
    const rm = (k: string) => rechargeMax[k] * Math.pow(0.8, upgrades.recharge);
    ringStyle(fire.ring, 1 - charge.fire / rm('fire'), '#f08a3c', charge.fire <= 0);
    ringStyle(ice.ring, 1 - charge.ice / rm('ice'), '#7fd0f0', charge.ice <= 0);
    ringStyle(bolt.ring, 1 - charge.lightning / rm('lightning'), '#f5c542', charge.lightning <= 0);
    ringStyle(tailRing, tailCharges > 0 ? 1 : 1 - charge.tail / rm('tail'), '#9b6be6', tailCharges > 0);
    for (const [w, btn] of [['fire', fire.b], ['ice', ice.b], ['lightning', bolt.b]] as [Weapon, HTMLElement][]) {
      btn.style.borderColor = armed === w ? 'var(--text)' : '';
      btn.style.boxShadow = armed === w ? '0 0 0 2px var(--text) inset' : '';
    }
    (gustBtn as HTMLButtonElement).disabled = gustUsed || phase !== 'play';
    (tailBtn as HTMLButtonElement).disabled = phase !== 'play';
  }

  function updateHead() {
    const strongs = head.querySelectorAll('strong');
    strongs[0].textContent = wave ? String(wave.n) : '-';
    strongs[1].textContent = String(Math.max(0, COINS_PER_WAVE - coinsLost));
    strongs[2].textContent = String(gold);
  }

  // ---------- geometry ----------
  const W = () => S.w, H = () => S.h;
  const laneX = (lane: number) => W() * (0.2 + lane * 0.3);
  const hoardY = () => H() * 0.78;
  const perchY = () => H() * 0.6;
  const perchX = () => W() / 2 + side * W() * 0.2;
  const fireRange = () => H() * 0.46;

  // ---------- start screen ----------
  function showPick() {
    phase = 'pick';
    clear(panel);
    panel.append(
      h('div.card', null, h('h3', null, 'You are the dragon.'), h('p.muted.small', null, 'Knights want your hoard. Tap a lane to breathe on it. Pick your scales:')),
      h('div.grid2', null, ...PALETTES.map(p => h('button.btn', { onclick: () => { palette = p; startWave(0); }, style: { borderColor: p.body, borderWidth: '3px' } }, h('span', { style: { width: '18px', height: '18px', borderRadius: '50%', background: p.body, display: 'inline-block' } }), p.name))),
    );
  }

  // ---------- waves ----------
  function startWave(i: number) {
    waveIdx = i; wave = waves[i];
    waveT = 0; spawned = 0; knights = []; rocks = []; fx = [];
    coinsLost = 0; frozen = [0, 0, 0]; gustUsed = false; scrollUsed = false; stun = 0;
    wavesPlayed++;
    phase = 'play';
    clear(panel);
    controls.style.display = '';
    updateHead(); renderControls();
    ctx.say(wave.n === 1 ? 'Here they come. Tap a lane to breathe fire.' : wave.catapult ? `Wave ${wave.n}. A catapult! Tap your perch to hop away from rocks.` : `Wave ${wave.n}. ${wave.spawns.length} knights.`);
  }

  function spawnDue() {
    if (!wave) return;
    const list = wave.spawns.slice(0, Math.max(1, wave.spawns.length - restartPenalty));
    while (spawned < list.length && list[spawned].delay <= waveT) {
      const s = list[spawned++];
      knights.push({ kind: s.kind, lane: s.lane, y: s.kind === 'catapult' ? -20 : -14, hp: KIND_HP[s.kind], state: 'march', stun: 0, hop: 0, lob: 3 });
    }
  }

  function loseWave() {
    // the dragon roars: every knight drops its loot and runs. Restart with one fewer knight.
    fx.push({ kind: 'roar', lane: 1, t: 0.8 });
    ctx.sound('buzz');
    ctx.say('ROAR! The knights drop the coins and run. Try the wave again, with one fewer knight.');
    restartPenalty++;
    totalLost += coinsLost;
    phase = 'break';
    setTimeout(() => { if (phase === 'break') startWave(waveIdx); }, 1400);
  }

  function clearWave() {
    wavesCleared++;
    restartPenalty = 0;
    ctx.sound('ding');
    if (waveIdx + 1 >= WAVES) { finish(); return; }
    phase = 'break';
    gold += 3;
    updateHead();
    showBreak();
  }

  function finish() {
    phase = 'done';
    controls.style.display = 'none';
    ctx.sound('chime');
    ctx.charm('charm.dragon');
    ctx.say('The hoard is safe. The knights will tell stories about you.');
    setTimeout(() => ctx.end({ attempts: wavesPlayed, correct: wavesCleared, perfect: totalLost === 0 && coinsLost === 0, durationMs: performance.now() - t0, notes: [`Knights routed: ${routed}`] }), 900);
  }

  // ---------- break menu ----------
  function showBreak() {
    clear(panel);
    const cost = { recharge: 8, wideFire: 10, secondTail: 12, gemShield: 6 };
    const buy = (k: keyof typeof cost, apply: () => void) => {
      if (gold < cost[k]) { ctx.say(`You need ${cost[k]} gold for that.`); return; }
      gold -= cost[k]; apply(); ctx.sound('pop'); updateHead(); showBreak();
    };
    const menu = h('div.grid2', null,
      h('button.btn.small', { disabled: upgrades.recharge >= 3 || gold < cost.recharge, onclick: () => buy('recharge', () => upgrades.recharge++) }, `Faster breath (${cost.recharge})`),
      h('button.btn.small', { disabled: upgrades.wideFire || gold < cost.wideFire, onclick: () => buy('wideFire', () => { upgrades.wideFire = true; }) }, upgrades.wideFire ? 'Wide fire: bought' : `Wider fire (${cost.wideFire})`),
      h('button.btn.small', { disabled: upgrades.secondTail || gold < cost.secondTail, onclick: () => buy('secondTail', () => { upgrades.secondTail = true; tailCharges = 2; }) }, upgrades.secondTail ? 'Two tails: bought' : `Second tail sweep (${cost.secondTail})`),
      h('button.btn.small', { disabled: upgrades.gemShield >= 1 || gold < cost.gemShield, onclick: () => buy('gemShield', () => upgrades.gemShield++) }, upgrades.gemShield ? 'Gem shield: ready' : `Gem shield (${cost.gemShield})`),
    );
    panel.append(
      h('div.card', null, h('h3', null, `Wave ${waveIdx + 1} cleared!`), h('p.muted.small', null, `Hoard gold: ${gold}. Spend it on upgrades, or save it.`)),
      menu,
      ...(scrollUsed ? [] : [h('button.btn.block', { onclick: showScroll }, 'Read a Scroll of Knowledge (+gems, full recharge)')]),
      h('button.btn.primary.block', { onclick: () => startWave(waveIdx + 1) }, `Next wave (${waveIdx + 2})`),
    );
  }

  function nextQuestion(): Question {
    const quiz = QUIZ.filter(q => q.level <= 2 && !used.has(q.id));
    const clues = CLUE_WORDS.filter(w => w.tier === 1 && !used.has('clue:' + w.word));
    if (quiz.length && (ctx.rng() < 0.6 || !clues.length)) {
      const q = pick(ctx.rng, quiz); used.add(q.id);
      const right = q.choices[q.answer];
      const choices = shuffle(ctx.rng, q.choices.slice());
      return { q: q.q, choices, answer: choices.indexOf(right), why: q.why };
    }
    const w = pick(ctx.rng, clues); used.add('clue:' + w.word);
    const others = shuffle(ctx.rng, CLUE_WORDS.filter(x => x.tier === 1 && x.word !== w.word)).slice(0, 3).map(x => x.word);
    const choices = shuffle(ctx.rng, [w.word, ...others]);
    return { q: `${w.clues[0]} ${w.clues[1]} What is it?`, choices, answer: choices.indexOf(w.word), why: w.clues[4] };
  }

  function showScroll() {
    scrollUsed = true;
    const q = nextQuestion();
    clear(panel);
    panel.append(
      h('div.card', null, h('p.muted.small', null, 'A dusty scroll from the hoard asks:'), h('h3', null, q.q)),
      h('div.grid2', null, ...q.choices.map((c, i) => h('button.btn', { onclick: () => answer(i) }, c))),
    );
    function answer(i: number) {
      if (i === q.answer) {
        ctx.sound('chime');
        ctx.award(10, 'scroll of knowledge');
        charge.fire = charge.ice = charge.lightning = charge.tail = 0; tailCharges = upgrades.secondTail ? 2 : 1;
        ctx.say(`Right! ${q.why} Every breath is recharged.`);
      } else {
        ctx.sound('buzz');
        ctx.say(`Not quite. ${q.why}`);
      }
      showBreak();
    }
  }

  // ---------- actions ----------
  function rm(k: string) { return rechargeMax[k] * Math.pow(0.8, upgrades.recharge); }

  function breathe(lane: number) {
    if (phase !== 'play' || stun > 0) { if (stun > 0) ctx.say('Dizzy from that rock! One moment.'); return; }
    facingLane = lane;
    if (charge[armed] > 0) { ctx.sound('tick'); return; }
    charge[armed] = rm(armed);
    breathT = 0.35;
    if (armed === 'fire') {
      fx.push({ kind: 'fire', lane, t: 0.35 });
      let hits = 0;
      for (const k of knights) {
        if (k.state === 'flee') continue;
        const inLane = k.lane === lane, adjacent = Math.abs(k.lane - lane) === 1 && upgrades.wideFire;
        const range = inLane ? fireRange() : fireRange() * 0.55;
        if ((inLane || adjacent) && k.y > hoardY() - range) { hit(k, 1); hits++; }
      }
      if (hits) ctx.sound('pop');
    } else if (armed === 'ice') {
      frozen[lane] = 3;
      fx.push({ kind: 'ice', lane, t: 3 });
      for (const k of knights) if (k.lane === lane && k.state === 'carry') dropCoin(k);
      ctx.sound('pop');
    } else {
      fx.push({ kind: 'bolt', lane, t: 0.25 });
      let hits = 0;
      for (const k of knights) if (k.lane === lane && k.state !== 'flee') { hit(k, 1); hits++; }
      if (hits) ctx.sound('pop');
    }
    renderControls();
  }

  function hit(k: Knight, dmg: number) {
    if (k.state === 'carry') dropCoin(k);
    k.hp -= dmg;
    if (k.hp <= 0) { k.state = 'flee'; routed++; gold += k.kind === 'shield' || k.kind === 'catapult' ? 3 : 2; updateHead(); }
    else k.stun = 0.4;
  }

  function dropCoin(k: Knight) {
    if (k.state !== 'carry') return;
    k.state = 'flee';
    coinsLost = Math.max(0, coinsLost - 1);
    updateHead();
  }

  function tailSweep() {
    if (phase !== 'play') return;
    if (tailCharges <= 0) { ctx.sound('tick'); return; }
    tailCharges--;
    if (tailCharges <= 0) charge.tail = rm('tail');
    fx.push({ kind: 'sweep', lane: 1, t: 0.4 });
    let n = 0;
    for (const k of knights) {
      if (k.state === 'flee' || k.kind === 'catapult') continue;
      if (k.y > hoardY() - H() * 0.22) { if (k.state === 'carry') dropCoin(k); k.y -= H() * 0.3; k.stun = 0.8; n++; }
    }
    ctx.sound(n ? 'pop' : 'tick');
    ctx.say(n ? `Tail sweep! ${n} knight${n === 1 ? '' : 's'} sent tumbling.` : 'Tail sweep. Nobody was close enough.');
    renderControls();
  }

  function wingGust() {
    if (phase !== 'play' || gustUsed) return;
    gustUsed = true;
    fx.push({ kind: 'gust', lane: 1, t: 0.5 });
    for (const k of knights) if (k.state !== 'flee' && k.kind !== 'catapult') { k.y -= H() * 0.18; k.stun = 0.5; }
    ctx.sound('pop');
    ctx.say('Wing gust! The whole wave staggers back.');
    renderControls();
  }

  function hop() {
    side = side === 1 ? -1 : 1;
    ctx.sound('tick');
  }

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      if (phase !== 'play') return;
      // tap near the perch hops; otherwise breathe on the nearest lane
      if (Math.abs(p.x - perchX()) < W() * 0.14 && Math.abs(p.y - perchY()) < H() * 0.12) { hop(); return; }
      const lane = Math.max(0, Math.min(LANES - 1, Math.round((p.x / W() - 0.2) / 0.3)));
      breathe(lane);
    },
  });

  // ---------- update ----------
  function update(dt: number) {
    flap += dt;
    if (msgT > 0) msgT -= dt;
    for (const k of Object.keys(charge)) if (charge[k] > 0) { charge[k] -= dt; if (charge[k] <= 0) { charge[k] = 0; if (k === 'tail') tailCharges = upgrades.secondTail ? 2 : 1; } }
    if (breathT > 0) breathT -= dt;
    if (stun > 0) stun -= dt;
    for (let i = 0; i < LANES; i++) if (frozen[i] > 0) frozen[i] -= dt;
    fx = fx.filter(f => (f.t -= dt) > 0);
    if (phase !== 'play' || !wave) { if (Math.floor(flap * 10) % 6 === 0) renderControls(); return; }
    waveT += dt;
    spawnDue();
    const trackSpeed = H() * 0.065 * wave.speedMul;
    for (const k of knights) {
      if (k.stun > 0) { k.stun -= dt; continue; }
      if (k.state === 'flee') { k.y -= trackSpeed * 2.2 * dt; continue; }
      if (frozen[k.lane] > 0) continue;
      if (k.kind === 'catapult') {
        if (k.y < H() * 0.12) k.y += trackSpeed * KIND_SPEED.catapult * dt;
        else { k.lob -= dt; if (k.lob <= 0) { k.lob = 4; rocks.push({ lane: k.lane, side, t: 0, dur: 1.8 }); } }
        continue;
      }
      if (k.state === 'march') {
        k.y += trackSpeed * KIND_SPEED[k.kind] * dt;
        if (k.y >= hoardY()) { k.state = 'carry'; coinsLost++; ctx.sound('buzz'); updateHead(); }
      } else if (k.state === 'carry') {
        k.y -= trackSpeed * KIND_SPEED[k.kind] * 0.8 * dt;
        if (k.y < -20) {
          // the coin leaves the keep
          if (upgrades.gemShield > 0) { upgrades.gemShield--; coinsLost = Math.max(0, coinsLost - 1); ctx.say('The gem shield caught that coin!'); updateHead(); }
          k.state = 'flee';
        }
      }
    }
    for (const r of rocks) {
      r.t += dt;
      if (r.t >= r.dur) {
        if (r.side === side && stun <= 0) { stun = 1.5; ctx.sound('buzz'); ctx.say('Bonk! A rock. Hop to the other side next time.'); }
      }
    }
    rocks = rocks.filter(r => r.t < r.dur);
    knights = knights.filter(k => !(k.state === 'flee' && k.y < -40));
    if (coinsLost >= COINS_PER_WAVE) { loseWave(); return; }
    const list = wave.spawns.slice(0, Math.max(1, wave.spawns.length - restartPenalty));
    if (spawned >= list.length && knights.every(k => k.state === 'flee' || k.kind === 'catapult' && k.hp <= 0)) {
      if (!knights.length || knights.every(k => k.state === 'flee')) clearWave();
      else if (knights.every(k => k.kind === 'catapult' || k.state === 'flee')) { // a catapult left alone cannot win; let it slink off
        knights = knights.filter(k => k.kind !== 'catapult'); clearWave();
      }
    } else if (spawned >= list.length && knights.every(k => k.state === 'flee' || k.kind === 'catapult')) {
      // only catapults remain: they can lob rocks but never take a coin, so the wave is won
      knights = knights.filter(k => k.kind !== 'catapult'); clearWave();
    }
    if (Math.floor(flap * 10) % 3 === 0) renderControls();
  }

  // ---------- draw ----------
  function draw() {
    const c = S.ctx, w = W(), hh = H();
    // sky, mountains, ground
    c.fillStyle = '#2d3a6b'; c.fillRect(0, 0, w, hh);
    c.fillStyle = '#3f4f8a'; c.beginPath(); c.moveTo(0, hh * 0.35); c.lineTo(w * 0.2, hh * 0.1); c.lineTo(w * 0.4, hh * 0.3); c.lineTo(w * 0.62, hh * 0.08); c.lineTo(w * 0.85, hh * 0.32); c.lineTo(w, hh * 0.18); c.lineTo(w, hh * 0.4); c.lineTo(0, hh * 0.4); c.fill();
    c.fillStyle = '#6b7a4e'; c.fillRect(0, hh * 0.38, w, hh);
    // lanes
    for (let i = 0; i < LANES; i++) {
      const x = laneX(i);
      c.fillStyle = frozen[i] > 0 ? '#bfe8ff' : '#c9a87c';
      roundRect(c, x - w * 0.075, 0, w * 0.15, hoardY() + 6, 8); c.fill();
      if (frozen[i] > 0) { c.fillStyle = 'rgba(255,255,255,.5)'; for (let y = 10; y < hoardY(); y += 26) c.fillRect(x - w * 0.06, y, w * 0.12, 3); }
    }
    // cave mouth and hoard
    c.fillStyle = '#2a2a2e'; c.beginPath(); c.ellipse(w / 2, hh * 0.9, w * 0.42, hh * 0.2, 0, Math.PI, 0); c.fill();
    c.fillStyle = '#1b1b20'; c.fillRect(0, hh * 0.9, w, hh * 0.1);
    for (let i = 0; i < 9; i++) { c.fillStyle = i % 3 === 0 ? '#4f8fe6' : '#f5c542'; c.beginPath(); c.arc(w / 2 + (i - 4) * w * 0.045, hh * 0.885 - Math.abs(i - 4) * -4 - (i % 2) * 8, w * 0.028, 0, 7); c.fill(); }
    if (upgrades.gemShield > 0) { c.strokeStyle = '#3bb3a9'; c.lineWidth = 3; c.beginPath(); c.ellipse(w / 2, hh * 0.88, w * 0.24, hh * 0.08, 0, 0, 7); c.stroke(); }
    // hoard line
    c.strokeStyle = 'rgba(255,255,255,.25)'; c.setLineDash([6, 6]); c.beginPath(); c.moveTo(0, hoardY()); c.lineTo(w, hoardY()); c.stroke(); c.setLineDash([]);
    // effects behind knights
    for (const f of fx) {
      if (f.kind === 'fire') {
        const x = laneX(f.lane), top = hoardY() - fireRange();
        c.fillStyle = 'rgba(240,138,60,.75)'; c.beginPath(); c.moveTo(perchX(), perchY() - 10); c.lineTo(x - w * 0.09, top); c.lineTo(x + w * 0.09, top); c.closePath(); c.fill();
        c.fillStyle = 'rgba(245,197,66,.6)'; c.beginPath(); c.moveTo(perchX(), perchY() - 10); c.lineTo(x - w * 0.04, top + 30); c.lineTo(x + w * 0.04, top + 30); c.closePath(); c.fill();
        if (upgrades.wideFire) { c.fillStyle = 'rgba(240,138,60,.3)'; for (const d of [-1, 1]) { const ln = f.lane + d; if (ln < 0 || ln > 2) continue; c.beginPath(); c.moveTo(perchX(), perchY() - 10); c.lineTo(laneX(ln) - w * 0.07, hoardY() - fireRange() * 0.55); c.lineTo(laneX(ln) + w * 0.07, hoardY() - fireRange() * 0.55); c.closePath(); c.fill(); } }
      } else if (f.kind === 'bolt') {
        const x = laneX(f.lane); c.strokeStyle = '#f5c542'; c.lineWidth = 4; c.beginPath(); c.moveTo(perchX(), perchY() - 10);
        for (let y = perchY() - 30; y > 0; y -= 28) c.lineTo(x + ((y / 28) % 2 ? 12 : -12), y); c.stroke();
      } else if (f.kind === 'sweep') {
        c.fillStyle = 'rgba(155,107,230,.35)'; c.fillRect(0, hoardY() - hh * 0.22, w, hh * 0.22);
      } else if (f.kind === 'gust') {
        c.fillStyle = 'rgba(59,179,169,.25)'; c.fillRect(0, 0, w, hoardY());
      } else if (f.kind === 'roar') {
        c.fillStyle = `rgba(226,87,76,${f.t * 0.4})`; c.fillRect(0, 0, w, hh);
      }
    }
    // knights
    for (const k of knights) drawKnight(c, k, w, hh);
    // rocks
    for (const r of rocks) {
      const p = r.t / r.dur, sx = laneX(r.lane), sy = hh * 0.12, ex = w / 2 + r.side * w * 0.2, ey = perchY();
      const x = sx + (ex - sx) * p, y = sy + (ey - sy) * p - Math.sin(p * Math.PI) * hh * 0.25;
      c.fillStyle = '#6d6b67'; c.beginPath(); c.arc(x, y, 9, 0, 7); c.fill();
    }
    drawDragon(c, perchX(), perchY(), w);
    // dizzy stars
    if (stun > 0) { c.fillStyle = '#f5c542'; for (let i = 0; i < 3; i++) { const a = flap * 6 + i * 2.1; c.beginPath(); c.arc(perchX() + Math.cos(a) * 26, perchY() - 48 + Math.sin(a) * 8, 4, 0, 7); c.fill(); } }
  }

  function drawKnight(c: CanvasRenderingContext2D, k: Knight, w: number, hh: number) {
    const x = laneX(k.lane), y = k.y, s = w / 412;
    c.save(); c.translate(x, y);
    if (k.state === 'flee') c.globalAlpha = 0.7;
    if (k.kind === 'catapult') {
      c.fillStyle = '#8a5a3b'; c.fillRect(-18 * s, -6 * s, 36 * s, 14 * s);
      c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(-12 * s, 10 * s, 5 * s, 0, 7); c.arc(12 * s, 10 * s, 5 * s, 0, 7); c.fill();
      c.strokeStyle = '#b48a5a'; c.lineWidth = 4 * s; c.beginPath(); c.moveTo(0, -4 * s); c.lineTo(14 * s, -26 * s); c.stroke();
      c.fillStyle = '#6d6b67'; c.beginPath(); c.arc(14 * s, -28 * s, 6 * s, 0, 7); c.fill();
      if (k.hp < KIND_HP.catapult) { c.fillStyle = '#e2574c'; c.fillRect(-18 * s, 12 * s, 36 * s * (k.hp / KIND_HP.catapult), 3); }
      c.restore(); return;
    }
    if (k.kind === 'horse') { c.fillStyle = '#8a5a3b'; roundRect(c, -16 * s, -2 * s, 32 * s, 12 * s, 5 * s); c.fill(); c.fillRect(-14 * s, 10 * s, 4 * s, 8 * s); c.fillRect(10 * s, 10 * s, 4 * s, 8 * s); }
    // body
    c.fillStyle = k.kind === 'shield' ? '#9a9aa3' : '#b7cdf2'; roundRect(c, -8 * s, -14 * s, 16 * s, 18 * s, 4 * s); c.fill();
    // helmet
    c.fillStyle = '#9a9aa3'; c.beginPath(); c.arc(0, -18 * s, 7 * s, 0, 7); c.fill();
    c.fillStyle = '#2a2a2e'; c.fillRect(-5 * s, -19 * s, 10 * s, 3 * s);
    // legs
    c.fillStyle = '#2a2a2e'; c.fillRect(-6 * s, 4 * s, 4 * s, 8 * s); c.fillRect(2 * s, 4 * s, 4 * s, 8 * s);
    if (k.kind === 'shield') { c.fillStyle = k.hp > 1 ? '#e2574c' : '#8e2626'; c.beginPath(); c.arc(-12 * s, -6 * s, 8 * s, 0, 7); c.fill(); c.fillStyle = '#f5c542'; c.fillRect(-14 * s, -7 * s, 4 * s, 2 * s); }
    if (k.kind === 'ladder') { c.strokeStyle = '#b48a5a'; c.lineWidth = 2 * s; c.beginPath(); c.moveTo(10 * s, -30 * s); c.lineTo(10 * s, 6 * s); c.moveTo(16 * s, -30 * s); c.lineTo(16 * s, 6 * s); for (let yy = -26; yy < 6; yy += 8) { c.moveTo(10 * s, yy * s); c.lineTo(16 * s, yy * s); } c.stroke(); }
    if (k.state === 'carry') { c.fillStyle = '#f5c542'; c.beginPath(); c.arc(10 * s, -24 * s, 5 * s, 0, 7); c.fill(); }
    if (k.stun > 0 && k.state !== 'flee') { c.fillStyle = '#f6f3ec'; c.beginPath(); c.arc(0, -30 * s, 3 * s, 0, 7); c.fill(); }
    c.restore();
  }

  function drawDragon(c: CanvasRenderingContext2D, x: number, y: number, w: number) {
    const s = w / 412;
    const face = laneX(facingLane) < x ? -1 : 1;
    const wingUp = Math.sin(flap * 6) * 10 * s;
    c.save(); c.translate(x, y); c.scale(face, 1);
    // tail
    c.strokeStyle = palette.body; c.lineWidth = 9 * s; c.lineCap = 'round'; c.beginPath(); c.moveTo(-10 * s, 4 * s); c.quadraticCurveTo(-46 * s, 10 * s, -52 * s, -18 * s); c.stroke();
    c.fillStyle = palette.belly; c.beginPath(); c.moveTo(-50 * s, -26 * s); c.lineTo(-60 * s, -14 * s); c.lineTo(-44 * s, -14 * s); c.fill();
    // wings
    c.fillStyle = palette.wing;
    c.beginPath(); c.moveTo(-6 * s, -14 * s); c.lineTo(-40 * s, -44 * s - wingUp); c.lineTo(-30 * s, -10 * s); c.lineTo(-18 * s, -36 * s - wingUp); c.lineTo(-8 * s, -6 * s); c.fill();
    c.beginPath(); c.moveTo(6 * s, -14 * s); c.lineTo(30 * s, -46 * s - wingUp); c.lineTo(26 * s, -10 * s); c.lineTo(14 * s, -34 * s - wingUp); c.lineTo(8 * s, -6 * s); c.fill();
    // body
    c.fillStyle = palette.body; c.beginPath(); c.ellipse(0, 0, 26 * s, 18 * s, 0, 0, 7); c.fill();
    c.fillStyle = palette.belly; c.beginPath(); c.ellipse(4 * s, 6 * s, 14 * s, 9 * s, 0, 0, 7); c.fill();
    // neck and head
    c.strokeStyle = palette.body; c.lineWidth = 12 * s; c.beginPath(); c.moveTo(16 * s, -6 * s); c.quadraticCurveTo(30 * s, -22 * s, 36 * s, -26 * s); c.stroke();
    c.fillStyle = palette.body; c.beginPath(); c.ellipse(42 * s, -30 * s, 14 * s, 10 * s, 0, 0, 7); c.fill();
    c.fillStyle = palette.wing; c.beginPath(); c.moveTo(34 * s, -38 * s); c.lineTo(30 * s, -50 * s); c.lineTo(40 * s, -40 * s); c.fill(); c.beginPath(); c.moveTo(44 * s, -39 * s); c.lineTo(46 * s, -52 * s); c.lineTo(50 * s, -40 * s); c.fill();
    c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(46 * s, -32 * s, 2.5 * s, 0, 7); c.fill();
    if (breathT > 0) { c.fillStyle = armed === 'fire' ? '#f08a3c' : armed === 'ice' ? '#7fd0f0' : '#f5c542'; c.beginPath(); c.arc(58 * s, -28 * s, 7 * s, 0, 7); c.fill(); }
    // feet on the perch
    c.fillStyle = palette.wing; c.fillRect(-14 * s, 14 * s, 8 * s, 6 * s); c.fillRect(6 * s, 14 * s, 8 * s, 6 * s);
    c.restore();
    // perch rock
    c.fillStyle = '#6d6b67'; roundRect(c, x - 30 * s, y + 18 * s, 60 * s, 14 * s, 6 * s); c.fill();
    c.fillStyle = 'rgba(255,255,255,.2)'; roundRect(c, x - 32 * s, y + 16 * s, 64 * s, 4 * s, 2 * s); c.fill();
  }

  const loop = makeLoop(update, draw);
  loop.start();
  showPick();
  ctx.say('Choose your dragon.');

  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'fun.dragon-keep',
  title: 'Dragon Keep',
  blurb: 'You are the dragon. Breathe fire, ice and lightning to keep the knights off your hoard.',
  category: 'fun',
  skills: [],
  levelRange: [1, 10],
  unlock: { gems: 300 },
  mount,
};
export default mod;
